import { useState, useEffect, useCallback } from 'react'

export interface TypographySettings {
  fontFamily: 'system' | 'serif' | 'sans-serif' | 'mono'
  fontSize: number // 14-24
  lineHeight: number // 1.4-2.0
  contentWidth: 'narrow' | 'medium' | 'wide' | 'full'
}

const DEFAULTS: TypographySettings = {
  fontFamily: 'serif',
  fontSize: 18,
  lineHeight: 1.7,
  contentWidth: 'medium'
}

const STORAGE_KEY = 'readl8r_typography'

export const FONT_FAMILIES = [
  { value: 'system', label: 'System', css: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  { value: 'serif', label: 'Serif', css: 'Georgia, Cambria, "Times New Roman", serif' },
  { value: 'sans-serif', label: 'Sans', css: '"Helvetica Neue", Arial, sans-serif' },
  { value: 'mono', label: 'Mono', css: '"SF Mono", Consolas, "Liberation Mono", monospace' },
] as const

export const CONTENT_WIDTHS = [
  { value: 'narrow', label: 'Narrow', maxWidth: '560px' },
  { value: 'medium', label: 'Medium', maxWidth: '680px' },
  { value: 'wide', label: 'Wide', maxWidth: '800px' },
  { value: 'full', label: 'Full', maxWidth: '100%' },
] as const

export function useTypography() {
  const [settings, setSettings] = useState<TypographySettings>(() => {
    if (typeof window === 'undefined') return DEFAULTS

    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      return saved ? { ...DEFAULTS, ...JSON.parse(saved) } : DEFAULTS
    } catch {
      return DEFAULTS
    }
  })

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
    } catch {
      // localStorage might be full or disabled
    }
  }, [settings])

  // Apply CSS variables to document
  useEffect(() => {
    const root = document.documentElement
    const fontConfig = FONT_FAMILIES.find(f => f.value === settings.fontFamily)
    const widthConfig = CONTENT_WIDTHS.find(w => w.value === settings.contentWidth)

    root.style.setProperty('--reader-font-family', fontConfig?.css || FONT_FAMILIES[1].css)
    root.style.setProperty('--reader-font-size', `${settings.fontSize}px`)
    root.style.setProperty('--reader-line-height', String(settings.lineHeight))
    root.style.setProperty('--reader-max-width', widthConfig?.maxWidth || '680px')
  }, [settings])

  const updateSetting = useCallback(<K extends keyof TypographySettings>(
    key: K,
    value: TypographySettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }))
  }, [])

  const reset = useCallback(() => {
    setSettings(DEFAULTS)
  }, [])

  return {
    settings,
    updateSetting,
    reset,
    FONT_FAMILIES,
    CONTENT_WIDTHS
  }
}
